import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { apiFetch } from "../../../core/utils/api";
import { SecondaryButton } from "../../../design-system";
import { PlayIcon, XIcon } from "../../../design-system/icons";
import { typography, surfaces, borders, textColors } from "../../../design-system/tokens";

const ANIMATION_OPTIONS = [
  { value: null, label: "None" },
  { value: "fade", label: "Fade" },
  { value: "slide", label: "Slide" },
  { value: "zoom", label: "Zoom" },
  { value: "light_reveal", label: "Light reveal" },
  { value: "curtain_reveal", label: "Curtain reveal" },
  { value: "blur_fade", label: "Blur + fade" },
  { value: "flip_3d", label: "Flip (3D)" },
  { value: "swipe", label: "Swipe" },
  { value: "scale_blur_pop", label: "Scale blur pop" },
];

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const normalizeDuration = (raw) => {
  if (raw === null || raw === undefined || raw === "") return null;
  const n = typeof raw === "number" ? raw : parseFloat(String(raw));
  if (!Number.isFinite(n)) return null;
  // Keep 0.1 resolution
  const rounded = Math.round(n * 10) / 10;
  return clamp(rounded, 0.5, 7.0);
};

const hasAnyAnimationEnabled = ({
  entry_animation,
  page_transition_animation,
}) => Boolean(entry_animation) || Boolean(page_transition_animation);

const buildPreviewDetail = ({
  websiteId,
  entry_animation,
  entry_duration,
  page_transition_animation,
  page_transition_duration,
  trigger,
}) => ({
  websiteId,
  animation: {
    entry_animation,
    entry_duration,
    page_transition_animation,
    page_transition_duration,
  },
  trigger,
  ts: Date.now(),
});

const WebsiteAnimationsPanel = ({ websiteId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [entryAnimation, setEntryAnimation] = useState(null);
  const [entryDuration, setEntryDuration] = useState(null);
  const [pageTransitionAnimation, setPageTransitionAnimation] = useState(null);
  const [pageTransitionDuration, setPageTransitionDuration] = useState(null);

  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [saveError, setSaveError] = useState("");

  const hydratedRef = useRef(false);
  const saveTimerRef = useRef(null);
  const lastSavedSignatureRef = useRef(null);

  const endpoint = useMemo(() => {
    if (!websiteId) return null;
    return `/api/websites/${websiteId}/animation/update/`;
  }, [websiteId]);

  const computeSignature = useCallback(
    ({
      entry_animation = entryAnimation,
      entry_duration = entryDuration,
      page_transition_animation = pageTransitionAnimation,
      page_transition_duration = pageTransitionDuration,
    } = {}) =>
      hasAnyAnimationEnabled({
        entry_animation,
        page_transition_animation,
      })
        ? JSON.stringify({
            entry_animation: entry_animation ?? null,
            entry_duration: entry_animation ? normalizeDuration(entry_duration) : null,
            page_transition_animation: page_transition_animation ?? null,
            page_transition_duration: page_transition_animation
              ? normalizeDuration(page_transition_duration)
              : null,
          })
        : "null",
    [
      entryAnimation,
      entryDuration,
      pageTransitionAnimation,
      pageTransitionDuration,
    ],
  );

  const dispatchPreview = useCallback(
    (trigger, overrides = {}) => {
      if (!websiteId) return;
      const nextEntryAnimation =
        overrides.entryAnimation !== undefined ? overrides.entryAnimation : entryAnimation;
      const nextEntryDuration =
        overrides.entryDuration !== undefined ? overrides.entryDuration : entryDuration;
      const nextPageAnimation =
        overrides.pageTransitionAnimation !== undefined
          ? overrides.pageTransitionAnimation
          : pageTransitionAnimation;
      const nextPageDuration =
        overrides.pageTransitionDuration !== undefined
          ? overrides.pageTransitionDuration
          : pageTransitionDuration;
      try {
        window.dispatchEvent(
          new CustomEvent(
            "editor:website-animations:preview",
            {
              detail: buildPreviewDetail({
                websiteId,
                entry_animation: nextEntryAnimation,
                entry_duration: nextEntryDuration,
                page_transition_animation: nextPageAnimation,
                page_transition_duration: nextPageDuration,
                trigger,
              }),
            },
          ),
        );
      } catch (_) {
        // Best-effort only.
      }
    },
    [
      websiteId,
      entryAnimation,
      entryDuration,
      pageTransitionAnimation,
      pageTransitionDuration,
    ],
  );

  // Initial fetch (use the update endpoint as a lightweight read)
  useEffect(() => {
    let cancelled = false;
    const fetchInitial = async () => {
      if (!endpoint) return;
      setLoading(true);
      setLoadError("");
      try {
        const res = await apiFetch(endpoint, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || "Failed to load website animations");
        }
        const data = await res.json();
        const anim = data?.animation || null;

        if (cancelled) return;
        const nextEntryAnimation = anim?.entry_animation ?? null;
        const nextEntryDuration = normalizeDuration(anim?.entry_duration ?? null);
        const nextPageAnimation = anim?.page_transition_animation ?? null;
        const nextPageDuration = normalizeDuration(anim?.page_transition_duration ?? null);

        setEntryAnimation(nextEntryAnimation);
        setEntryDuration(nextEntryDuration);
        setPageTransitionAnimation(nextPageAnimation);
        setPageTransitionDuration(nextPageDuration);

        // Prevent an immediate autosave caused by hydration.
        lastSavedSignatureRef.current = computeSignature({
          entry_animation: nextEntryAnimation,
          entry_duration: nextEntryDuration,
          page_transition_animation: nextPageAnimation,
          page_transition_duration: nextPageDuration,
        });
        hydratedRef.current = true;
      } catch (e) {
        if (cancelled) return;
        setLoadError(String(e?.message || "Failed to load website animations"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchInitial();
    return () => {
      cancelled = true;
    };
  }, [endpoint]);

  // Auto-fill defaults when enabling an animation without a duration selected.
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (entryAnimation && entryDuration == null) {
      setEntryDuration(2.0);
    }
  }, [entryAnimation, entryDuration]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    if (pageTransitionAnimation && pageTransitionDuration == null) {
      setPageTransitionDuration(1.0);
    }
  }, [pageTransitionAnimation, pageTransitionDuration]);

  const saveNow = useCallback(async () => {
    if (!endpoint) return;
    setSaveState("saving");
    setSaveError("");
    try {
      const enabled = hasAnyAnimationEnabled({
        entry_animation: entryAnimation,
        page_transition_animation: pageTransitionAnimation,
      });

      const payload = enabled
        ? {
            entry_animation: entryAnimation,
            entry_duration: entryAnimation ? normalizeDuration(entryDuration) : null,
            page_transition_animation: pageTransitionAnimation,
            page_transition_duration: pageTransitionAnimation
              ? normalizeDuration(pageTransitionDuration)
              : null,
          }
        : null; // canonical delete

      const res = await apiFetch(endpoint, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to save website animations");
      }
      const data = await res.json();
      const anim = data?.animation || null;

      // Normalize from backend response (applies defaults)
      const nextEntryAnimation = anim?.entry_animation ?? null;
      const nextEntryDuration = normalizeDuration(anim?.entry_duration ?? null);
      const nextPageAnimation = anim?.page_transition_animation ?? null;
      const nextPageDuration = normalizeDuration(anim?.page_transition_duration ?? null);

      setEntryAnimation(nextEntryAnimation);
      setEntryDuration(nextEntryDuration);
      setPageTransitionAnimation(nextPageAnimation);
      setPageTransitionDuration(nextPageDuration);

      lastSavedSignatureRef.current = computeSignature({
        entry_animation: nextEntryAnimation,
        entry_duration: nextEntryDuration,
        page_transition_animation: nextPageAnimation,
        page_transition_duration: nextPageDuration,
      });

      setSaveState("saved");
    } catch (e) {
      setSaveState("error");
      setSaveError(String(e?.message || "Failed to save website animations"));
    }
  }, [
    endpoint,
    entryAnimation,
    entryDuration,
    pageTransitionAnimation,
    pageTransitionDuration,
  ]);

  // Debounced autosave on changes
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (!endpoint) return;

    const sig = computeSignature();
    if (sig === lastSavedSignatureRef.current) {
      return;
    }

    // Clear prior debounce
    if (saveTimerRef.current) {
      try {
        window.clearTimeout(saveTimerRef.current);
      } catch (_) {}
    }

    setSaveState("idle");
    setSaveError("");

    saveTimerRef.current = window.setTimeout(() => {
      saveNow();
    }, 450);

    return () => {
      if (saveTimerRef.current) {
        try {
          window.clearTimeout(saveTimerRef.current);
        } catch (_) {}
      }
    };
  }, [
    endpoint,
    entryAnimation,
    entryDuration,
    pageTransitionAnimation,
    pageTransitionDuration,
    saveNow,
  ]);

  const header = (
    <div className="p-6 pb-2">
      <div
        className={[
          "flex justify-between items-center mb-1 sticky top-0 z-20 py-1.5",
          "bg-transparent",
          `border-b ${borders.default}`,
        ].join(" ")}
      >
        <div className="flex flex-col">
          <h2 className={`${typography.h4} ${textColors.primary}`}>Website Animations</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className={`${textColors.muted} ${textColors.hover}`}
            title="Back"
            aria-label="Back to element editor"
          >
            <XIcon size="md" />
          </button>
        </div>
      </div>
    </div>
  );

  const renderAnimationSelect = (value, onChange) => (
    <select
      value={value ?? ""}
      onChange={(e) => {
        const v = e.target.value || null;
        onChange(v);
      }}
      className={[
        "w-full rounded-md px-3 py-2",
        "border shadow-sm transition-colors duration-200 focus:outline-none",
        surfaces.primary,
        borders.default,
        textColors.primary,
        "placeholder-gray-400 dark:placeholder-dark-500",
        "focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-500 focus:border-blue-600 dark:focus:border-blue-500",
      ].join(" ")}
    >
      {ANIMATION_OPTIONS.map((opt) => (
        <option key={String(opt.value)} value={opt.value ?? ""}>
          {opt.label}
        </option>
      ))}
    </select>
  );

  const renderDurationControls = ({ enabled, value, onChange, defaultValue, onPreview }) => {
    const display = normalizeDuration(value) ?? defaultValue;
    return (
      <div className="grid grid-cols-1 gap-3">
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={0.5}
            max={7}
            step={0.1}
            value={display}
            disabled={!enabled}
            onChange={(e) => onChange(normalizeDuration(e.target.value))}
            className="w-full"
          />
          <input
            type="number"
            min={0.5}
            max={7}
            step={0.1}
            value={enabled ? display : ""}
            disabled={!enabled}
            onChange={(e) => onChange(normalizeDuration(e.target.value))}
            className={[
              "w-24 rounded-md px-2 py-1.5",
              "border shadow-sm transition-colors duration-200 focus:outline-none",
              surfaces.primary,
              borders.default,
              textColors.primary,
              "placeholder-gray-400 dark:placeholder-dark-500",
              "focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-500 focus:border-blue-600 dark:focus:border-blue-500",
            ].join(" ")}
          />
          <span className={`${typography.bodySmall} ${textColors.muted} whitespace-nowrap`}>sec</span>
          <SecondaryButton
            onClick={onPreview}
            disabled={!enabled}
            leftIcon={<PlayIcon size="sm" />}
          >
            Preview
          </SecondaryButton>
        </div>
        {!enabled ? (
          <div className={`${typography.bodySmall} ${textColors.muted}`}>
            Select an animation to enable duration controls.
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div data-tutorial-id="edit-panel" className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        {header}

        <div className="p-6 pt-2 space-y-8">
          {loadError ? (
            <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3">
              <div className={`${typography.bodySmall} text-red-700 dark:text-red-200`}>
                {loadError}
              </div>
            </div>
          ) : null}

          {saveError ? (
            <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3">
              <div className={`${typography.bodySmall} text-red-700 dark:text-red-200`}>
                {saveError}
              </div>
            </div>
          ) : null}

          <section className="space-y-4">
            <div>
              <div className={`${typography.h5} ${textColors.primary}`}>Entry animation</div>
              <div className={`${typography.bodySmall} ${textColors.muted} mt-1`}>
                Plays when the website loads (public), or when you preview in the editor.
              </div>
            </div>

            <div className="space-y-2">
              <div className={`${typography.bodySmall} ${textColors.secondary}`}>Type</div>
              {renderAnimationSelect(entryAnimation, (v) => {
                setEntryAnimation(v);
              })}
            </div>

            <div className="space-y-2">
              <div className={`${typography.bodySmall} ${textColors.secondary}`}>Duration</div>
              {renderDurationControls({
                enabled: !!entryAnimation,
                value: entryDuration,
                onChange: (v) => {
                  setEntryDuration(v);
                },
                defaultValue: 2.0,
                onPreview: () => dispatchPreview("entry"),
              })}
            </div>
          </section>

          <section className="space-y-4">
            <div>
              <div className={`${typography.h5} ${textColors.primary}`}>Page transition</div>
              <div className={`${typography.bodySmall} ${textColors.muted} mt-1`}>
                Plays when navigating between pages (public), or when you preview in the editor.
              </div>
            </div>

            <div className="space-y-2">
              <div className={`${typography.bodySmall} ${textColors.secondary}`}>Type</div>
              {renderAnimationSelect(pageTransitionAnimation, (v) => {
                setPageTransitionAnimation(v);
              })}
            </div>

            <div className="space-y-2">
              <div className={`${typography.bodySmall} ${textColors.secondary}`}>Duration</div>
              {renderDurationControls({
                enabled: !!pageTransitionAnimation,
                value: pageTransitionDuration,
                onChange: (v) => {
                  setPageTransitionDuration(v);
                },
                defaultValue: 1.0,
                onPreview: () => dispatchPreview("page_transition"),
              })}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default WebsiteAnimationsPanel;

